<template>
  <div
    class="table-wrapper"
    :style="cssProps"
  >
    <v-data-table
      :model-value="selectedOffices"
      :page="currentPage"
      :headers="headers"
      :items="offices"
      :loading="loading"
      :items-per-page="itemsPerPage"
      show-select
      item-value="id"
      return-object
      class="office-table text-title-medium"
      @update:model-value="$emit('update:selectedOffices', $event)"
      @update:page="$emit('update:currentPage', $event)"
    >
      <template #[`item.office`]="{ item }">
        <p class="mb-0">{{ item.office }} {{ item.ambassador ? `- ${item.ambassador}` : '' }}</p>
      </template>
      <template #bottom>
        <div
          v-if="totalPages > 1"
          class="pagination-container"
        >
          <div class="pagination-controls">
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="pagination-btn"
              :class="{ active: currentPage === page, ellipsis: page === '...' }"
              :disabled="page === '...'"
              @click="page !== '...' && $emit('update:currentPage', Number(page))"
            >
              {{ page }}
            </button>
          </div>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'

import type { Office } from '../composables/useOfficeSelection'

interface Props {
  offices: Office[]
  selectedOffices: Office[]
  loading: boolean
  currentPage: number
  visiblePages: (number | string)[]
  totalPages: number
  itemsPerPage: number
}

defineProps<Props>()

defineEmits<{
  'update:selectedOffices': [offices: Office[]]
  'update:currentPage': [page: number]
}>()

const { t } = useI18n()

const headers = computed(() => [
  { title: t('content-creator.office-selection.office-name'), key: 'office', sortable: false },
  { title: t('content-creator.office-selection.city'), key: 'city', sortable: false },
  { title: t('content-creator.office-selection.state'), key: 'state', sortable: false },
])

const theme = useTheme()
const cssProps = computed(() => ({
  '--primary-color': theme.current.value.colors.primary,
}))
</script>

<style scoped>
.table-wrapper {
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.office-table {
  border: none;
  height: 100%;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.office-table :deep(.v-table) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.office-table :deep(.v-table__wrapper) {
  flex: 1 1 0;
  overflow-y: auto !important;
  min-height: 0;
}

.office-table :deep(thead tr th) {
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: rgba(var(--v-theme-on-surface)) !important;
  background-color: #ffffff !important;
  border-bottom: 0.5px solid #9e9e9e !important;
  padding: 7px 16px !important;
}

.office-table :deep(thead tr th:first-child),
.office-table :deep(tbody tr td:first-child) {
  padding-left: 16px !important;
  padding-right: 8px !important;
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
}

.office-table :deep(tbody tr) {
  height: 48px;
}

.office-table :deep(tbody tr td) {
  height: 48px;
  padding: 7px 16px !important;
  border-bottom: 0.5px solid #9e9e9e !important;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: rgba(var(--v-theme-on-surface));
}

.office-table :deep(.v-selection-control__wrapper) {
  width: 18px;
  height: 18px;
}

.office-table :deep(.v-selection-control__input) {
  width: 18px;
  height: 18px;
}

.office-table :deep(.v-selection-control__input .v-icon) {
  font-size: 18px;
}

.office-table :deep(.v-selection-control--dirty .v-icon) {
  color: var(--primary-color) !important;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  width: 18px;
  height: 18px;
  min-width: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface));
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  background-color: #f5f5f5;
}

.pagination-btn:disabled {
  cursor: default;
}

.pagination-btn.ellipsis {
  cursor: default;
  background: transparent !important;
}

.pagination-btn.active {
  background-color: #add3d2;
  color: rgba(var(--v-theme-on-surface));
}
</style>
